import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import styles from "./OurApi.module.scss";

// Inline SVGs to replace local file imports and guarantee compilation
const Icons = {
  Identity: () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="1em"
      height="1em"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
      <circle cx="9" cy="7" r="4"></circle>
      <polyline points="16 11 18 13 22 9"></polyline>
    </svg>
  ),
  Financial: () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="1em"
      height="1em"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <ellipse cx="12" cy="5" rx="9" ry="3"></ellipse>
      <path d="M3 5V19A9 3 0 0 0 21 19V5"></path>
      <path d="M3 12A9 3 0 0 0 21 12"></path>
    </svg>
  ),
  Fraud: () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="1em"
      height="1em"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
      <line x1="12" y1="8" x2="12" y2="12"></line>
      <line x1="12" y1="16" x2="12.01" y2="16"></line>
    </svg>
  ),
  Compliance: () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="1em"
      height="1em"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
      <polyline points="14 2 14 8 20 8"></polyline>
      <path d="M9 15L11 17L15 11"></path>
    </svg>
  ),
  Banking: () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="1em"
      height="1em"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polygon points="12 2 2 7 22 7 12 2"></polygon>
      <polyline points="2 17 22 17"></polyline>
      <polyline points="2 22 22 22"></polyline>
      <line x1="6" y1="17" x2="6" y2="7"></line>
      <line x1="10" y1="17" x2="10" y2="7"></line>
      <line x1="14" y1="17" x2="14" y2="7"></line>
      <line x1="18" y1="17" x2="18" y2="7"></line>
    </svg>
  ),
  Document: () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="1em"
      height="1em"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
      <polyline points="14 2 14 8 20 8"></polyline>
      <circle cx="10" cy="13" r="2"></circle>
      <line x1="11.4" y1="14.4" x2="15" y2="18"></line>
    </svg>
  ),
};

const CATEGORIES = [
  {
    id: "identity",
    title: "Identity Verification",
    blurb:
      "Aadhaar, PAN, Voter ID, Driving Licence, Passport, Face Match, Liveness.",
    cta: "Explore identity APIs",
    icon: <Icons.Identity />,
  },
  {
    id: "financial",
    title: "Financial Data",
    blurb: "Bank statement analysis, GST returns, ITR, EPF, MCA company data.",
    cta: "Explore financial data APIs",
    icon: <Icons.Financial />,
  },
  {
    id: "fraud",
    title: "Fraud Detection",
    blurb:
      "Device fingerprinting, behavioural signals, velocity checks, risk scoring.",
    cta: "Explore fraud APIs",
    icon: <Icons.Fraud />,
  },
  {
    id: "compliance",
    title: "Compliance",
    blurb: "AML, PEP, sanctions screening, adverse media, DPDPA consent.",
    cta: "Explore compliance APIs",
    icon: <Icons.Compliance />,
  },
  {
    id: "banking",
    title: "Banking Intelligence",
    blurb:
      "Account number verification, UPI handle verify, name-match, penny-drop.",
    cta: "Explore banking APIs",
    icon: <Icons.Banking />,
  },
  {
    id: "document",
    title: "Document Verification",
    blurb: "OCR, authenticity, tamper detection, cross-source match.",
    cta: "Explore document APIs",
    icon: <Icons.Document />,
  },
];

const OurApi = () => {
  const reduceMotion = useReducedMotion();
  const navigate = useNavigate();

  const listVariants = {
    hidden: { opacity: reduceMotion ? 1 : 0 },
    show: {
      opacity: 1,
      transition: reduceMotion
        ? { duration: 0 }
        : { staggerChildren: 0.08, delayChildren: 0.06 },
    },
  };

  const itemVariants = {
    hidden: { opacity: reduceMotion ? 1 : 0, y: reduceMotion ? 0 : 16 },
    show: {
      opacity: 1,
      y: 0,
      transition: reduceMotion
        ? { duration: 0 }
        : { type: "spring", stiffness: 400, damping: 32 },
    },
  };

  return (
    <>
      <section
        className={styles["page-api-section"]}
        id="platform-overview"
        aria-labelledby="api-showcase-title"
      >
        <div className={styles["page-api-mesh"]} aria-hidden />
        <div className={styles["page-api-glow"]} aria-hidden />

        <div className="sec-container">
          <header className={styles["page-api-header"]}>
            <span className="page-eyebrow">
              One platform. Hundreds of APIs. Seven SaaS products.
            </span>
            <h2 id="api-showcase-title" className="gen-h2">
              Every verification your regulated business needs — under one roof.
            </h2>
            <p className={styles["page-api-lede"]}>
              Doql brings the full stack of financial and identity verification
              under one contract, one dashboard, and one audit trail. Integrate
              once. Scale across every product, every journey, every
              jurisdiction you operate in.
            </p>
          </header>

          <motion.ul
            className={styles["page-api-grid"]}
            variants={listVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-50px" }}
          >
            {CATEGORIES.map((item) => (
              <motion.li
                key={item.id}
                className={styles["page-api-card"]}
                variants={itemVariants}
              >
                <div className={styles["page-api-accent"]} aria-hidden />
                <div className={styles["page-api-icon"]} aria-hidden>
                  {item.icon}
                </div>
                <div className={styles["page-api-body"]}>
                  <h3 className={styles["page-api-card-title"]}>
                    {item.title}
                  </h3>
                  <p className={styles["page-api-card-blurb"]}>{item.blurb}</p>
                  <button
                    type="button"
                    className={styles["page-api-link"]}
                    onClick={() => navigate("/api-catalogue-home")}
                  >
                    {item.cta} <span aria-hidden>→</span>
                  </button>
                </div>
              </motion.li>
            ))}
          </motion.ul>
        </div>
      </section>
    </>
  );
};

export default OurApi;
